import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CustomColorPickerPopover } from "components/colorpicker";
import Input from "components/form/input/Input";
import Toggle from "components/form/toggle/Toggle";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { cloneDeep } from "lodash";
import {
  useWebsiteBuilderDebouncedUpdate,
  WebsiteBuilderDebouncedUpdateTime,
} from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";
import React, { useMemo, useState } from "react";
import { IWebsiteBuilderSelectOption, renderWebsiteBuilderSelectOptions } from "../../../../../helpers/renderWebsiteBuilderSelectOptions";
import { useTranslation } from "react-i18next";
import { websiteBuilderUpdateContainerSettings } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { Select } from "components/select/index";
import { WEBSITE_BUILDER_PORTAL } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";
import {
  IWebsiteBuilderMarqueeSettings,
  TWebsiteBuilderMarqueeDirection,
  TWebsiteBuilderMarqueeFontSize,
  WEBSITE_BUILDER_MARQUEE_MIN_FONT_SIZE,
  WEBSITE_BUILDER_MARQUEE_MAX_FONT_SIZE,
  WEBSITE_BUILDER_MARQUEE_MIN_DURATION,
  WEBSITE_BUILDER_MARQUEE_MAX_DURATION,
} from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderMarquee";
import { WebsiteBuilderNumberInput } from "pages/secure/facility/websiteBuilder/editor/components/numberInput/WebsiteBuilderNumberInput";
import { WebsiteBuilderRange } from "pages/secure/facility/websiteBuilder/editor/components/range/WebsiteBuilderRange";
import { MarqueeSettingsManageItems } from "./MarqueeSettingsManageItems";
import { MarqueeSettingsEditIconView } from "./MarqueeSettingsEditIconView";
import { MarqueeSettingsEditLinkView } from "./MarqueeSettingsEditLinkView";
import { MarqueeSettingsEditParagraphView } from "./MarqueeSettingsEditParagraphView";
import { WebsiteBuilderPanelContent } from "pages/secure/facility/websiteBuilder/editor/shell/panel/content/WebsiteBuilderPanelContent";

interface IMarqueeSettingsProps {
  id: string;
  settings: IWebsiteBuilderMarqueeSettings;
  closeSettings: () => void;
}

interface IMarqueeSettingsNoSubView {
  type: "none";
}

interface IMarqueeSettingsEditIconSubView {
  type: "edit-icon";
  itemId: string;
}

interface IMarqueeSettingsEditLinkSubView {
  type: "edit-link";
  itemId: string;
}

interface IMarqueeSettingsEditParagraphSubView {
  type: "edit-paragraph";
  itemId: string;
}

export type TMarqueeSettingsSubView =
  | IMarqueeSettingsNoSubView
  | IMarqueeSettingsEditIconSubView
  | IMarqueeSettingsEditLinkSubView
  | IMarqueeSettingsEditParagraphSubView;

export function MarqueeSettings(props: IMarqueeSettingsProps) {
  const { id, settings, closeSettings } = props;

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);

  const [updatedSettings, setUpdatedSettings] = useState<IWebsiteBuilderMarqueeSettings>(() => cloneDeep(settings));

  const [subView, setSubView] = useState<TMarqueeSettingsSubView>({ type: "none" });

  const [moreSettingsOpen, setMoreSettingsOpen] = useState(false);

  const directionOptions: IWebsiteBuilderSelectOption<TWebsiteBuilderMarqueeDirection>[] = useMemo(() => {
    return [
      {
        label: t("Right to left"),
        value: "right-to-left",
      },
      {
        label: t("Left to right"),
        value: "left-to-right",
      },
    ];
  }, []);

  function dispatchSettings(
    prevSettings: IWebsiteBuilderMarqueeSettings,
    nextSettings: IWebsiteBuilderMarqueeSettings,
  ) {
    dispatch(
      websiteBuilderUpdateContainerSettings(activePageId, deviceView, id, {
        id,
        prevContainerSettings: {
          type: "marquee",
          settings: cloneDeep(prevSettings),
        },
        nextContainerSettings: {
          type: "marquee",
          settings: cloneDeep(nextSettings),
        },
      }),
    );
  }

  const { handleUpdateState, handleDebouncedUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: settings,
    updatedState: updatedSettings,
    setUpdatedState: setUpdatedSettings,
    dispatchState: dispatchSettings,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.MEDIUM,
  });

  function renderManageItems() {
    return (
      <MarqueeSettingsManageItems
        marqueeId={id}
        items={updatedSettings.items}
        handleUpdateState={handleUpdateState}
        setSubView={setSubView}
      />
    );
  }

  function getResponsiveFontSize(desktop: number): TWebsiteBuilderMarqueeFontSize {
    if (desktop == null || desktop < 16) {
      return { desktop, tablet: desktop, mobile: desktop };
    } else if (desktop < 20) {
      return { desktop, tablet: 14, mobile: 14 };
    } else if (desktop < 24) {
      return { desktop, tablet: 18, mobile: 16 };
    } else if (desktop < 32) {
      return { desktop, tablet: 22, mobile: 16 };
    } else {
      return { desktop, tablet: 24, mobile: 18 };
    }
  }

  function renderFontSettings() {
    return (
      <>
        {deviceView === "desktop" ? (
          <WebsiteBuilderNumberInput
            label={t("Font size (px)")}
            initialValue={updatedSettings.fontSize[deviceView]}
            min={WEBSITE_BUILDER_MARQUEE_MIN_FONT_SIZE}
            max={WEBSITE_BUILDER_MARQUEE_MAX_FONT_SIZE}
            required={false}
            onChange={fontSize => {
              handleDebouncedUpdateState(prev => ({
                ...prev,
                fontSize: getResponsiveFontSize(fontSize),
              }));
            }}
          />
        ) : (
          <Input
            label={t("Font size (px)")}
            value={updatedSettings.fontSize[deviceView] ?? ""}
            readOnly
          />
        )}
      </>
    );
  }

  function renderColorSettings() {
    return (
      <>
        <CustomColorPickerPopover
          title={t("Background")}
          hexColor={updatedSettings.backgroundColor}
          updateHexColor={backgroundColor =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              backgroundColor,
            }))
          }
          portalParentId={WEBSITE_BUILDER_PORTAL}
        />
      </>
    );
  }

  function renderSeparatorSettings() {
    return (
      <>
        <div className="marquee-settings-show-separator-container">
          <Toggle
            labelRight={t("Show separator")}
            checked={updatedSettings.showSeparator}
            onChange={e =>
              handleUpdateState(prev => ({
                ...prev,
                showSeparator: e.target.checked,
              }))
            }
          />
        </div>
        <CustomColorPickerPopover
          title={t("Separator")}
          hexColor={updatedSettings.separatorColor}
          updateHexColor={separatorColor =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              separatorColor,
            }))
          }
          portalParentId={WEBSITE_BUILDER_PORTAL}
        />
      </>
    );
  }

  function renderMovementSettings() {
    return (
      <>
        <Select
          label={t("Scroll movement direction")}
          defaultValue={updatedSettings.direction}
          onChange={(direction: TWebsiteBuilderMarqueeDirection) =>
            handleUpdateState(prev => ({
              ...prev,
              direction,
            }))
          }
          usePortal
          portalId={WEBSITE_BUILDER_PORTAL}
        >
          {renderWebsiteBuilderSelectOptions(directionOptions)}
        </Select>
        <WebsiteBuilderRange
          label={`${t("Scroll duration")} (ms)`}
          min={WEBSITE_BUILDER_MARQUEE_MIN_DURATION}
          max={WEBSITE_BUILDER_MARQUEE_MAX_DURATION}
          step={500}
          value={updatedSettings.duration}
          setValue={duration =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              duration,
            }))
          }
        />
      </>
    );
  }

  function renderMoreSettings() {
    return (
      <div className="marquee-settings-more-settings">
        <button
          type="button"
          className="more-settings-trigger"
          aria-expanded={moreSettingsOpen}
          onClick={() => setMoreSettingsOpen(prev => !prev)}
        >
          <div>{t("More settings")}</div>
          <FontAwesomeIcon icon={moreSettingsOpen ? faChevronUp : faChevronDown} />
        </button>
        {moreSettingsOpen && (
          <div className="more-settings-content">
            {renderMovementSettings()}
          </div>
        )}
      </div>
    );
  }

  function goBackToParentView() {
    setSubView({ type: "none" });
  }

  function renderView() {
    switch (subView.type) {
      case "edit-icon": {
        const item = updatedSettings.items.find(item => item.id === subView.itemId);

        return item?.type === "icon" ? (
          <MarqueeSettingsEditIconView
            item={item}
            handleUpdateState={handleUpdateState}
            handleDebouncedUpdateState={handleDebouncedUpdateState}
            onBack={goBackToParentView}
            closeSettings={closeSettings}
          />
        ) : null;
      }
      case "edit-link": {
        const item = updatedSettings.items.find(item => item.id === subView.itemId);

        if (item?.type == "link") {
          return (
            <MarqueeSettingsEditLinkView
              item={item}
              handleUpdateState={handleUpdateState}
              handleDebouncedUpdateState={handleDebouncedUpdateState}
              onBack={goBackToParentView}
              closeSettings={closeSettings}
            />
          );
        } else {
          return null;
        }
      }
      case "edit-paragraph": {
        const item = updatedSettings.items.find(item => item.id === subView.itemId);

        if (item?.type === "paragraph") {
          return (
            <MarqueeSettingsEditParagraphView
              item={item}
              handleDebouncedUpdateState={handleDebouncedUpdateState}
              onBack={goBackToParentView}
              closeSettings={closeSettings}
            />
          );
        } else {
          return null;
        }
      }
      case "none":
      default:
        return (
          <WebsiteBuilderPanelContent
            title={t("Scrolling Banner settings")}
            closePanel={closeSettings}
            sections={[
              renderManageItems(),
              renderFontSettings(),
              renderColorSettings(),
              renderSeparatorSettings(),
              renderMoreSettings(),
            ]}
          />
        );
    }
  }

  return (
    <div className="website-builder-marquee-settings">
      {renderView()}
    </div>
  );
}